import { Component, DestroyRef, input, output, signal, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { IntegrationError, EnrichedIntegrationError } from 'src/app/models/integration-error';
import { IntegrationErrorsService } from 'src/app/services/integration-errors.service';
import { ToastService } from 'src/app/services/toast.service';
import { passUntilDestroyed } from 'src/app/utilities/rx-operators';
import { LTHelperUtils } from 'src/app/utilities/lt-helper.utils';
import { LTButtonComponent } from 'src/app/features/lt-shared-ui/lt-button/components/lt-button/lt-button.component';

@Component({
  selector: 'app-lt-error-detail-view',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    LTButtonComponent,
  ],
  templateUrl: './lt-error-detail-view.html',
  styleUrl: './lt-error-detail-view.scss',
  providers: [IntegrationErrorsService],
})
export class LTErrorDetailViewComponent {
  private readonly errorService = inject(IntegrationErrorsService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  //errorData = input.required<IntegrationError>();
  errorData = input.required<EnrichedIntegrationError>();
  errorUpdated = output<IntegrationError>(); // Emit updated error back to parent

  // Internal state signals
  private _showJiraInput = signal(false);
  private _jiraUrl = signal('');
  private _isUpdating = signal(false);
  private _isGeneratingJira = signal(false);
  private _savedJiraUrl = signal<string | null>(null);

  // Computed signals
  showJiraInput = computed(() => this._showJiraInput());
  jiraUrl = computed(() => this._jiraUrl());
  isUpdating = computed(() => this._isUpdating());
  isGeneratingJira = computed(() => this._isGeneratingJira());

  isJiraOperationInProgress = computed(() => this.isUpdating() || this.isGeneratingJira());

  hasJiraUrl = computed(() => {
    const saved = this._savedJiraUrl();
    if (saved !== null) return !!(saved && saved.trim() && saved !== 'null');
    const url = this.errorData().jiraStoryURL;
    return !!(url && url.trim() && url !== 'null');
  });

  savedJiraUrl = computed(() => 
    {
      var url = this._savedJiraUrl() ?? this.errorData().jiraStoryURL;
      if (url.startsWith("https://") == false && url.startsWith("http://") == false)
      {
        url = "https://" + url;
      }
      return url;
    }
  );
  
  isResolved = computed(() => {
    return this.errorData().statusName === 'Resolved';
  });
  
  clientNavigationRoute = computed(() => {
    //const error = this.errorData();
    //return ['/editConfigV2', error.clientInformationId, error.integrationId];
    return ['/editConfigV2', this.clientInformationId(), this.integrationId()];
  });

  clientInformationId = computed(() => {
    const err = this.errorData();
    if (err.clientInformationId) {
      return err.clientInformationId;
    }
    else if (err.integration?.clientInformationId) {
      return err.integration?.clientInformationId;
    }
    else if (err.client?.clientInformationId) {
      return err.client?.clientInformationId;
    }

    return null;
  });

  clientDisplayName = computed(() => {
    const err = this.errorData();
    if (err.clientName) {
      return err.clientName;
    }
    else if (err.clientDisplayName) {
      return err.clientDisplayName;
    }
    else if (err.client?.clientDisplayName) {
      return err.client?.clientDisplayName;
    }

    return null;
  });

  clientId = computed(() => {
    const err = this.errorData();
    if (err.clientId) {
      return err.clientId;
    }
    else if (err.integration?.clientId) {
      return err.integration?.clientId;
    }
    else if (err.client?.losTalkerClientId) {
      return err.client?.losTalkerClientId;
    }

    return null;
  });

  integrationId = computed(() => {
    const err = this.errorData();
    if (err.integrationId) {
      return err.integrationId;
    }
    else if (err.integration?.integrationId) {
      return err.integration?.integrationId;
    }
    
    return null;
  });

  dataSourceTypeId = computed(() => {
    const err = this.errorData();
    
    if (err.integration?.dataSourceTypeId) {
      return err.integration?.dataSourceTypeId;
    }
    else if (err.dataSourceTypeId) {
      return err.dataSourceTypeId;
    }
    
    return null;
  });

  integrationServerId = computed(() => {
    const err = this.errorData();
    if (err.integrationServerId) {
      return err.integrationServerId;
    }
    else if (err.integration?.serverId) {
      return err.integration?.serverId;
    }
    
    return null;
  });

  integrationDisplayName = computed(() => LTHelperUtils.getIntegrationDisplayName(this.errorData()));

  constructor() { }

  toggleJiraInput(): void {
    const currentState = this._showJiraInput();
    this._showJiraInput.set(!currentState);
    
    // Initialize with current URL when opening for edit
    if (!currentState && this.hasJiraUrl()) {
      this._jiraUrl.set(this.errorData().jiraStoryURL || '');
    } else if (!currentState) {
      this._jiraUrl.set('');
    }
  }
  
  updateJiraUrl(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this._jiraUrl.set(target.value);
  }
  
  saveJiraUrl(): void {
    const url = this.jiraUrl().trim();
    if (!url || this.isUpdating()) {
      return;
    }

    this._isUpdating.set(true);

    // Create updated error object with new JIRA URL
    const updatedError: IntegrationError = {
      ...this.errorData(),
      jiraStoryURL: url,
    };

    // Call service to update the error
    this.errorService.updateIntegrationError(updatedError)
      .pipe(passUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          // Success - update parent with new data
          this._savedJiraUrl.set(url);
          this.errorUpdated.emit(updatedError);
          this._showJiraInput.set(false);
          this._isUpdating.set(false);
          this.toastService.toastCreate('JIRA URL updated successfully', 'Success');
        },
        error: (error) => {
          // Error - show toast and keep form open
          this._isUpdating.set(false);
          this.toastService.toastCreate('Failed to update JIRA URL: ' + error, 'Warning', {
            autoClose: false,
          });
        },
      });
  }

  private generateShortMessage(): string {
    const item = this.errorData();
    
    // Logic from original function
    if (item.statusDescription != null && item.statusDescription != '') {
      return item.statusDescription;
    }
    else if (item.categoryName != null && item.categoryName != '') {
      return item.categoryName;
    }
    else {
      return 'Generated from LOSTalker Site';
    }
  }

  generateJiraTicket(){
    if (this.isJiraOperationInProgress()) {
      return;
    }

    this._isGeneratingJira.set(true);

    // Generate short message using original logic
    const shortMsg = this.generateShortMessage();
    const item = this.errorData();

    this.errorService.generateJiraTicketForError(item, shortMsg)
      .pipe(passUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (value: string) => {
          if (value == 'TEST Environment. No Issue Created.') {
            // Test environment - no actual ticket created
            this._isGeneratingJira.set(false);
            this.toastService.toastCreate('JIRA Ticket attempted to generate in Non-Production Environment. No issue sent to Jira. See logs for CreateIssue body information.', 'Danger', {
              autoClose: true,
              keepAfterRouteChange: true,
            });
          } else {
            const updatedError: IntegrationError = {
              ...item,
              jiraStoryURL: value,
            };

            this._savedJiraUrl.set(value);
            this.errorUpdated.emit(updatedError);
            this._isGeneratingJira.set(false);
            this.toastService.toastCreate('JIRA Ticket Generated and JIRA URL added to integrationError', 'Success', {
              autoClose: true,
              keepAfterRouteChange: true,
            });
          }

        },
        error: (_error) => {
          this._isGeneratingJira.set(false);
          this.toastService.toastCreate(
            'Failed To Generate JIRA Ticket and update JIRA URL for Error', 
            'Warning', { autoClose: false },
          );
        },
      });
  }

  formatDate(date: Date | string | null): string {
    if (!date) return 'N/A';
    
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      return dateObj.toLocaleDateString() + ', ' + dateObj.toLocaleTimeString();
    } catch {
      return 'Invalid Date';
    }
  }
}
